import React, { useEffect, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { withAsyncPaginate } from "react-select-async-paginate";

const summaryComponents = {
  DropdownIndicator: null,
};

const CreatableAsyncPaginate = withAsyncPaginate(CreatableSelect) as any;

export interface SelectInputProps {
  value: any[];
  onChange: (values: any[]) => void;
  options?: any[];
  isMulti?: boolean;
  isDisabled?: boolean;
  isSummary?: boolean;
  isClearable?: boolean;
  isSelect?: boolean;
  onCreateOption?: ((v: any) => void) | null;
  isOptionDisabled?: (o: any) => boolean;
  isValidNewOption?: (a: any) => boolean;
  loadOptions?: ((search: string, prevOptions: any[]) => Promise<any>) | null;
  getValueLabels?: ((values: any[]) => Promise<any[]>) | null;
  autoFocus?: boolean;
  placeholder?: string;
  components?: any;
  isSearchable?: boolean;
  isContactSet?: boolean;
  formatOptionLabel?: (o: any) => any;
  filterOption?: ((option: any, inputValue: string) => boolean) | undefined;
  maxLength?: number | null;
}

export default function SelectInput({
  value,
  onChange,
  options,
  isMulti = false,
  isDisabled = false,
  isSummary = false,
  isClearable = false,
  isSelect = false,
  onCreateOption = null,
  isOptionDisabled = () => false,
  isValidNewOption = () => false,
  loadOptions = null,
  getValueLabels = null,
  autoFocus = false,
  placeholder = "",
  components = null,
  isSearchable = true,
  isContactSet = false,
  formatOptionLabel = (o: any) => o.label,
  filterOption = undefined,
  maxLength = null,
}: SelectInputProps) {
  const [loading, setLoading] = useState(false);
  const ref = useRef<any>(null);
  const menuObserver = useRef<IntersectionObserver | null>(null);

  const [loadedOptions, setLoadedOptions] = useState(options || []);
  useEffect(() => {
    setLoadedOptions(options ?? []);
  }, [options]);

  const [valueObject, setValueObject] = useState<any>(null);

  useEffect(() => {
    if (!value || value.length === 0) {
      setValueObject(isMulti ? [] : null);
      return;
    }
    setLoading(true);
    if (getValueLabels && value[0] !== "") {
      (async () => {
        const valueLabels = await getValueLabels(value);
        setValueObject(valueLabels);
      })();
    } else {
      setValueObject(
        value.map((opt) => ({
          value: opt,
          label: loadedOptions?.find((o: any) => o.value === opt)?.label || opt,
        }))
      );
    }
    setLoading(false);
  }, [value]);

  useEffect(() => {
    if (ref.current && maxLength) {
      ref.current.inputRef.maxLength = maxLength;
    }
  }, []);

  const [alignRight, setAlignRight] = useState(0);

  const onMenuOpen = () => {
    const observeOnscreen = (entries: IntersectionObserverEntry[] = []) => {
      const { boundingClientRect, intersectionRect } = entries[0];
      const isOffscreen = boundingClientRect.width - intersectionRect.width;
      setAlignRight(isOffscreen);
    };

    setTimeout(() => {
      const menuList = ref.current?.menuListRef;
      if (menuList) {
        menuObserver.current = new IntersectionObserver(observeOnscreen);
        menuObserver.current.observe(menuList);
      }
    }, 1);
  };

  const onMenuClose = () => {
    setAlignRight(0);
    menuObserver.current?.disconnect?.();
  };

  const loadOptionsAndUpdate = async (
    search: string,
    prevOptions: readonly any[]
  ) => {
    if (loadOptions) {
      setLoading(true);
      const data = await loadOptions(search, prevOptions as any[]);
      setLoadedOptions([...(prevOptions as any[]), ...data.options]);
      setTimeout(() => {
        setLoading(false);
        onMenuOpen();
      }, 500);
      return {
        options: data.options,
        hasMore: data.hasMore,
      };
    }
    return {
      options: (options ?? []).filter((o: any) =>
        o.label.toLowerCase().includes(search.toLowerCase())
      ),
    };
  };

  return (
    <div data-select-input>
      <CreatableAsyncPaginate
      selectRef={ref}
      classNamePrefix="react-select"
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      classNames={{
        container: () => "form-control p-0",
        control: () => "border-0",
        multiValue: () => "bg-colored rounded-pill text-white icon-white",
        multiValueLabel: () => "text-white fw-bold px-2",
        multiValueRemove: () => "rounded-pill",
      }}
      menuPortalTarget={document.body}
      menuPlacement="auto"
      styles={{
        menu: (base: any) => ({
          ...base,
          width: "max-content",
          minWidth: "100%",
          maxWidth: "calc(100vw - 20px)",
          background: "var(--purple-gradient), white",
          boxShadow: "4px 4px 8px 0px #888",
          ...(alignRight && {
            marginLeft: -alignRight - 10,
            transition: "all 0.2s",
          }),
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        control: (base: any) => ({
          ...base,
          ...(isSummary && {
            backgroundColor: "transparent",
          }),
        }),
        singleValue: (base: any) => ({
          ...base,
          ...(isSummary && {
            color: "var(--bs-list-group-color)",
          }),
        }),
        valueContainer: (base: any) => ({
          ...base,
          ...(isSummary && {
            padding: 0,
          }),
        }),
        multiValueRemove: (base: any) => ({
          ...base,
          ...(isSummary && {
            display: "none",
          }),
        }),
      }}
      autoFocus={autoFocus}
      isMulti={isMulti}
      isDisabled={isDisabled || isSummary}
      isSearchable={isSearchable}
      isClearable={isClearable}
      isOptionDisabled={isOptionDisabled}
      isValidNewOption={isValidNewOption}
      formatOptionLabel={formatOptionLabel}
      defaultOptions
      loadOptions={loadOptionsAndUpdate}
      filterOption={filterOption}
      debounceTimeout={loadOptions ? 1000 : 0}
      isLoading={loading}
      placeholder={placeholder}
      value={valueObject}
      components={
        (isSelect && !isSummary && components) ? components : summaryComponents
      }
      noOptionsMessage={() => null}
      onChange={(o: any) => {
        let opts = o;
        if (opts === null) {
          opts = [];
        }
        if (!Array.isArray(opts)) {
          opts = [opts];
        }
        onChange(
          opts.map((opt: any) => opt.value).sort((a: any, b: any) => a - b)
        );
      }}
      onCreateOption={onCreateOption}
      />
    </div>
  );
}
